import { useEffect, useRef } from 'react';

/**
 * WorkOS AuthKit's initial silent token refresh can hang without its promise ever
 * settling (same root cause as the getAccessToken timeout in api-auth-provider.tsx),
 * leaving `isLoading` stuck `true` forever. Every loading gate in the app
 * (dashboard.lazy.tsx, index.tsx, login.tsx) blocks on that flag, so a hang here
 * shows the loading spinner indefinitely with no way out except a manual reload.
 * This mirrors that manual workaround: if `isLoading` doesn't clear within the
 * timeout, force one reload to give AuthKit a fresh start. Guarded by a
 * sessionStorage flag so a persistent outage (e.g. WorkOS itself down) doesn't
 * reload-loop the tab.
 */
const STUCK_LOADING_TIMEOUT_MS = 8000;
const RECOVERY_ATTEMPTED_KEY = 'authStuckRecoveryAttempted';

export function useStuckAuthRecovery(isLoading: boolean) {
  const timerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!isLoading) {
      sessionStorage.removeItem(RECOVERY_ATTEMPTED_KEY);
      return;
    }

    if (sessionStorage.getItem(RECOVERY_ATTEMPTED_KEY)) return;

    timerRef.current = window.setTimeout(() => {
      sessionStorage.setItem(RECOVERY_ATTEMPTED_KEY, '1');
      window.location.reload();
    }, STUCK_LOADING_TIMEOUT_MS);

    return () => window.clearTimeout(timerRef.current);
  }, [isLoading]);
}
